import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

interface Props {
  error: unknown;
}

/** This component will be used if an error occurs below any route defined with
 *  it passed as the errorComponent.
 *  It's required because Tanstack Router has a built-in one that it will use
 *  otherwise, which will not report to AppInsights, but prevent the error from
 *  bubbling up as far as AppInsightsErrorBoundary, which would.
 *  But AppInsightsErrorBoundary is still required as well, in case the error
 *  occurs outside of the RouterProvider.
 */
export function RouteErrorBoundary({ error }: Props) {
  const router = useRouter();
  const appInsights = router.options.context.appInsightsReactPlugin;

  useEffect(() => {
    if (appInsights && error) {
      // Normalize unknown to Error
      const err =
        error instanceof Error ? error : new Error(JSON.stringify(error));
      appInsights.trackException({ exception: err });
    }
  }, [error, appInsights]);

  return (
    <div style={{ padding: 24 }}>
      <h2>A routed error occurred and has been reported.</h2>
      <pre style={{ whiteSpace: "pre-wrap" }}>
        {(error instanceof Error && error.message) || String(error)}
      </pre>
    </div>
  );
}
